import { useMemo } from 'react';

import { PATIENT_SNAPSHOT_TAB_ANCHOR } from '@/app/hints';
import { useUserInfoQuery } from '@/entities/user-info';
import {
  formatPatientInfoKey,
  formatPatientInfoValue,
  getPatientInfoTableEntries,
} from '@/entities/user-info';
import { useHintAnchor } from '@/shared/ui/primitives';
import { Table, TableBody, TableCell, TableRow } from '@/shared/ui/shadcn/table';

interface PatientSnapshotTabProps {
  userId: string;
}

export function PatientSnapshotTab({ userId }: PatientSnapshotTabProps) {
  const userInfoQuery = useUserInfoQuery(userId);
  // Bound on the success branch only — loading/error states deliberately don't register.
  const snapshotAnchorRef = useHintAnchor(PATIENT_SNAPSHOT_TAB_ANCHOR);

  const rows = useMemo(() => {
    const patientInfo = userInfoQuery.data?.patientInfo;
    if (!patientInfo || typeof patientInfo !== 'object') return [];
    return getPatientInfoTableEntries(patientInfo);
  }, [userInfoQuery.data]);

  if (userInfoQuery.isLoading) {
    return <div className="p-3 text-sm text-muted-foreground">Loading snapshot…</div>;
  }

  if (userInfoQuery.isError) {
    return <div className="p-3 text-sm text-muted-foreground">Could not load snapshot.</div>;
  }

  return (
    <div ref={snapshotAnchorRef} className="h-full overflow-auto p-3">
      <Table>
        <TableBody>
          {rows.map(([key, value]) => (
            <TableRow key={key} className="border-b-0">
              <TableCell className="w-[180px] whitespace-nowrap font-medium text-muted-foreground">
                {formatPatientInfoKey(key)}
              </TableCell>
              <TableCell className="whitespace-normal">{formatPatientInfoValue(value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
